/* eslint-disable default-case */
import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check } from 'lucide-react';

const CustomSelect = ({
  options = [],
  value = '',
  onChange = () => {},
  placeholder = 'Select an option...',
  label = '',
  error = '',
  disabled = false,
  className = '',
  size = 'md',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const selectRef = useRef(null);

  const sizeClasses = {
    sm: 'h-7 min-h-7 px-2.5 text-xs leading-none',
    md: 'h-8 min-h-8 px-3 text-xs leading-none',
    lg: 'h-9 min-h-9 px-3 text-sm leading-none',
  };

  const baseClasses = 'box-border flex w-full items-center border border-border rounded-md text-ink focus:outline-none focus:ring-1 focus:ring-ink transition-colors bg-surface cursor-pointer';
  
  const inputClasses = `
    ${baseClasses}
    ${sizeClasses[size]}
    ${error ? 'border-red-300 focus:ring-red-500' : ''}
    ${disabled ? 'bg-surface-muted cursor-not-allowed opacity-50' : ''}
    ${className}
  `.trim();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleSelect = (option) => {
    const optionValue = typeof option === 'string' ? option : option.value;
    onChange(optionValue);
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (disabled) return;

    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (isOpen && highlightedIndex >= 0) {
          const filteredOptions = options;
          if (filteredOptions[highlightedIndex]) {
            handleSelect(filteredOptions[highlightedIndex]);
          }
        } else {
          setIsOpen(!isOpen);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else {
          setHighlightedIndex((prev) => 
            prev < options.length - 1 ? prev + 1 : prev
          );
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (isOpen) {
          setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
        }
        break;
    }
  };

  const getDisplayValue = () => {
    if (!value) return placeholder;
    const option = options.find(opt => {
      const optValue = typeof opt === 'string' ? opt : opt.value;
      return optValue === value;
    });
    if (!option) return value;
    return typeof option === 'string' ? option : option.label || option.name;
  };

  const isSelected = (option) => {
    const optionValue = typeof option === 'string' ? option : option.value;
    return value === optionValue;
  };

  return (
    <div className="w-full" ref={selectRef}>
      {label && (
        <label className="mb-1.5 block text-xs font-medium text-ink sm:text-sm">
          {label}
        </label>
      )}
      
      <div className="relative">
        <div
          className={inputClasses}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          onKeyDown={handleKeyDown}
          tabIndex={disabled ? -1 : 0}
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-controls={isOpen ? 'custom-select-options' : undefined}
        >
          <div className="flex h-full w-full items-center justify-between gap-2">
            <span className={`truncate ${!value ? 'text-ink-tertiary' : 'text-ink'}`}>
              {getDisplayValue()}
            </span>
            <ChevronDown className={`h-3.5 w-3.5 flex-shrink-0 text-ink-tertiary transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </div>
        </div>

        {isOpen && (
          <div 
            id="custom-select-options"
            className="absolute z-[12000] mt-1 max-h-60 w-full overflow-auto rounded-product-md border border-border bg-surface shadow-sm"
            role="listbox"
          >
            <div className="py-1">
              {options.length === 0 ? (
                <div className="px-3 py-2 text-center text-sm text-ink-secondary">
                  No options available
                </div>
              ) : (
                options.map((option, index) => {
                  const optionValue = typeof option === 'string' ? option : option.value;
                  const optionLabel = typeof option === 'string' ? option : option.label || option.name;
                  const isOptionSelected = isSelected(option);
                  const isHighlighted = index === highlightedIndex;

                  return (
                    <div
                      key={optionValue}
                      className={`flex cursor-pointer items-center justify-between px-3 py-1.5 text-xs transition-colors ${ isOptionSelected ? 'bg-ink text-white' : isHighlighted ? 'bg-surface-muted text-ink' : 'text-ink hover:bg-surface-muted' }`}
                      onClick={() => handleSelect(option)}
                      onMouseEnter={() => setHighlightedIndex(index)}
                    >
                      <span>{optionLabel}</span>
                      {isOptionSelected && (
                        <Check className="ml-2 h-4 w-4 flex-shrink-0" />
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
      
      {error && (
        <p className="mt-1 text-xs sm:text-sm text-red-600">{error}</p>
      )}
    </div>
  );
};

export default CustomSelect;

